import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import ModalUsers, { DeleteUsers } from '@/components/ModalUsers';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '@/redux/slices/users/usersSlices';
import { fetchAllCompany } from '@/redux/slices/company/companySlices';
import { selectUserData } from '@reduxSlices/user/userSlices';
import { Toast } from 'primereact/toast';
import { Paginator } from 'primereact/paginator';
import './styles.scss';

const Users = () => {
  const dispatch = useDispatch();
  const [visibleAddUsers, setVisibleAddUsers] = useState(false);
  const [usersData, setUsersData] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window?.innerWidth);
  const { items = [], counts, status, error } = useSelector((state) => state.users) ?? {};
  const { role } = useSelector(selectUserData) ?? {};
  const { allCompany } = useSelector((state) => state.company);
  const [paginationParams, setPaginationParams] = useState({ limit: 10, offset: 0 });
  const toast = useRef(null);

  const BodyTemplate = (rowData) => {
    const [visible, setVisible] = useState(false);
    return (
      <div className='card flex justify-content-start'>
        <Button
          type='button'
          icon='pi pi-pencil'
          rounded
          style={{ marginRight: '8px' }}
          onClick={() => editRow(rowData)}
        />

        <DeleteUsers
          visible={visible}
          open={() => {
            setVisible(true);
          }}
          hide={() => {
            setVisible(false);
          }}
          usersData={rowData}
          updateLoad={updateLoad(paginationParams)}
        />
      </div>
    );
  };

  const updateLoad = (params) => () => {
    setPaginationParams(params);
    dispatch(fetchUsers(params));
  };

  const editRow = (rowData) => {
    setUsersData(rowData);
    setVisibleAddUsers(true);
  };

  const dateBodyTemplate = (rowData) => {
    const date = new Date(rowData.created_at);
    return date.toLocaleDateString('uk', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <Tag
        value={rowData.role}
        severity={
          {
            admin: 'success',
            manager: 'info',
            inspector: 'warning',
          }[rowData.role]
        }
      />
    );
  };

  const cols = [
    ...(role === 'admin'
      ? [
          {
            field: 'company_name',
            header: 'Підриємство',
            style: { maxWidth: '320px', minWidth: '250px' },
            sortable: true,
          },
        ]
      : []),
    { field: 'login', header: 'Логін', style: { maxWidth: '320px' }, sortable: true },
    { field: 'name', header: "Ім'я", style: { maxWidth: '320px', minWidth: '200px' }, sortable: true },
    { field: 'tzd', header: 'Назва ТЗД', style: { maxWidth: '300px' }, sortable: true },
    { field: 'tzd_id', header: 'Індифікатор ТЗД', style: { maxWidth: '300px' }, sortable: true },
    {
      field: 'created_at',
      header: 'Дата створення',
      filterField: 'date',
      body: dateBodyTemplate,
      style: { maxWidth: '200px', minWidth: '180px' },
      sortable: true,
    },
    { field: 'role', header: 'Роль', style: { maxWidth: '250px' }, body: statusBodyTemplate, sortable: true },
  ];

  const openModalHeader = () => {
    setUsersData(null);
    setVisibleAddUsers(true);
  };

  const onPageChange = (e) => {
    const newOffset = e.first;
    updateLoad({ limit: e.rows, offset: newOffset })();
  };

  const header = (
    <div className='flex align-items-center justify-content-end gap-3'>
      <Button icon='pi pi-plus' onClick={openModalHeader} />
    </div>
  );

  const footer = (
    <Paginator
      first={paginationParams.offset}
      rows={paginationParams.limit}
      totalRecords={counts}
      onPageChange={onPageChange}
    />
  );

  useEffect(() => {
    dispatch(fetchUsers(paginationParams));
  }, [dispatch, paginationParams]);

  useEffect(() => {
    if (status === 'failed') {
      toast.current.show({ severity: 'error', summary: 'Error', detail: `${error}`, life: 5000 });
    }
  }, [error, status]);

  useEffect(() => {
    dispatch(fetchAllCompany());
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className='page'>
      <h2 className='page__title'>Управління користувачами</h2>
      <DataTable
        value={items}
        header={header}
        responsiveLayout='scroll'
        scrollable
        showGridlines
        scrollHeight='1000px'
        footer={footer}
        rows={paginationParams.limit}
        emptyMessage='Поки що немає даних'
      >
        {cols.map(({ field, header, ...props }, index) => (
          <Column key={index} field={field} header={header} {...props} />
        ))}
        <Column
          header='Редагування'
          body={BodyTemplate}
          frozen={windowWidth > 600}
          alignFrozen='right'
          style={{ maxWidth: '160px', minWidth: '140px' }}
        ></Column>
      </DataTable>

      <ModalUsers
        visible={visibleAddUsers}
        hide={() => setVisibleAddUsers(false)}
        updateLoad={updateLoad(paginationParams)}
        usersData={usersData}
        allCompany={allCompany}
      />
      <Toast ref={toast} />
    </div>
  );
};

export default Users;
